import { motion } from 'framer-motion';
import { FaFire } from 'react-icons/fa';

/**
 * Tarjeta de "Cardio recomendado" — Fase 3 Fit Evolution.
 *
 * Renderiza el plan de cardio (LISS + HIIT) generado por
 * `fit_evolution_cardio.recommend_cardio_plan` y persistido en
 * `workoutPlan.cardio_plan`. Es tolerante al shape legacy (`has_cardio`
 * plano + cardio_type + cardio_duration) para no romper planes viejos.
 */
export default function CardioRecommendedCard({ workoutPlan }) {
  const cp = workoutPlan?.cardio_plan || {};
  const hasV2 = cp && (cp.liss || cp.hiit || cp.format_summary);
  const shouldRender = cp.has_cardio || workoutPlan?.has_cardio;
  if (!shouldRender) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="mb-8"
      data-testid="cardio-recommended-card"
    >
      <div
        className="rounded-2xl p-6 backdrop-blur-xl border"
        style={{ background: 'rgba(15, 23, 42, 0.55)', borderColor: 'rgba(52, 211, 153, 0.25)' }}
      >
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #34D399, #059669)' }}>
            <FaFire className="text-white text-xl" />
          </div>
          <h3 className="text-xl font-extrabold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>Cardio recomendado</h3>
        </div>

        {hasV2 && (
          <div className="rounded-xl p-5 mb-4" style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(148, 163, 184, 0.15)' }}>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-4xl font-black" style={{ color: '#34D399' }} data-testid="cardio-sessions-count">{cp.sessions_per_week}</span>
              <span className="text-gray-400 text-sm font-semibold">sesiones por semana</span>
            </div>
            <p className="text-white font-semibold text-base mb-1" data-testid="cardio-format-summary">{cp.format_summary}</p>
            {cp.balance_note && <p className="text-gray-500 text-sm">{cp.balance_note}</p>}
          </div>
        )}

        {cp.recommendation && (
          <div className="rounded-xl p-4 mb-4" style={{ background: 'rgba(251, 191, 36, 0.08)', border: '1px solid rgba(251, 191, 36, 0.3)' }} data-testid="cardio-adjustment-note">
            <div className="flex items-start gap-2">
              <span className="text-amber-400 text-lg leading-tight">💡</span>
              <p className="text-amber-100 text-sm leading-snug"><b className="text-amber-300">Ajuste inteligente:</b> {cp.recommendation}</p>
            </div>
          </div>
        )}

        {cp.liss && (
          <div className="rounded-xl p-5 mb-4" style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(52, 211, 153, 0.35)' }} data-testid="cardio-liss-box">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-3 py-0.5 rounded-full text-xs font-black" style={{ background: 'rgba(52, 211, 153, 0.2)', color: '#34D399' }}>LISS</span>
              <h4 className="font-bold text-white">{cp.liss.title}</h4>
            </div>
            <p className="text-gray-400 text-sm leading-snug mb-3">{cp.liss.description}</p>
            <p className="text-emerald-300 text-xs font-bold uppercase mb-2 tracking-wide">Ejemplos:</p>
            <ul className="space-y-1.5">
              {(cp.liss.examples || []).map((ex, i) => (
                <li key={i} className="text-gray-200 text-sm">• {ex}</li>
              ))}
            </ul>
          </div>
        )}

        {cp.hiit && (
          <div className="rounded-xl p-5" style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(var(--brand-primary-glow), 0.35)' }} data-testid="cardio-hiit-box">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-3 py-0.5 rounded-full text-xs font-black" style={{ background: 'rgba(var(--brand-primary-glow), 0.2)', color: 'var(--brand-primary-soft)' }}>HIIT</span>
              <h4 className="font-bold text-white">{cp.hiit.title}</h4>
            </div>
            <p className="text-gray-400 text-sm leading-snug mb-3">{cp.hiit.description}</p>
            <p className="text-pink-300 text-xs font-bold uppercase mb-2 tracking-wide">Ejemplos para hacer HIIT (elige uno):</p>
            <ul className="space-y-1.5 mb-3">
              {(cp.hiit.examples || []).map((ex, i) => (
                <li key={i} className="text-gray-200 text-sm leading-snug">• {ex}</li>
              ))}
            </ul>
            {cp.hiit.tip && (
              <p className="text-gray-500 text-xs italic border-t border-white/5 pt-2">Tip: {cp.hiit.tip}</p>
            )}
          </div>
        )}

        {!hasV2 && workoutPlan?.has_cardio && (
          <>
            <div className="flex flex-wrap gap-3 text-sm">
              <span className="px-3 py-1 rounded-full font-semibold" style={{ background: 'rgba(52, 211, 153, 0.2)', color: '#34D399' }}>
                {workoutPlan.cardio_type?.toUpperCase() || 'Cardio'}
              </span>
              <span className="px-3 py-1 rounded-full font-semibold" style={{ background: 'rgba(148, 163, 184, 0.2)', color: '#94A3B8' }}>
                {workoutPlan.cardio_duration || 20} min
              </span>
            </div>
            {workoutPlan.cardio_notes && workoutPlan.cardio_notes.trim() && (
              <div
                className="mt-3 rounded-xl p-4"
                style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(52, 211, 153, 0.25)' }}
                data-testid="cardio-notes-legacy"
              >
                <p className="text-emerald-300 text-xs font-bold uppercase mb-1 tracking-wide">Notas de cardio</p>
                <p className="text-gray-200 text-sm whitespace-pre-line leading-snug">{workoutPlan.cardio_notes}</p>
              </div>
            )}
          </>
        )}
      </div>
    </motion.div>
  );
}
